import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  PersonOutline as PersonOutlineIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import Can from '../../components/Can';
import VendedorSelect from '../../components/VendedorSelect';
import { ROUTES } from '../../config/routes';
import crmCatalogoService from '../../services/crmCatalogoService';
import crmContactoService from '../../services/crmContactoService';
import type { CrmContactoListItem, CrmOrigen, CrmTag } from '../../types/crm';
import { getApiErrorMessage } from '../../utils/apiError';
import { initialsFromName, isRecentlyCreated } from '../../utils/crmUi';
import CrmContactosImportDialog from './components/CrmContactosImportDialog';

const CrmContactos: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [importOpen, setImportOpen] = useState(false);
  const [items, setItems] = useState<CrmContactoListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [nroVendedor, setNroVendedor] = useState<number | null>(null);
  const [origenId, setOrigenId] = useState<number | ''>('');
  const [tagId, setTagId] = useState<number | ''>('');
  const [soloProspectos, setSoloProspectos] = useState(false);
  const [origenes, setOrigenes] = useState<CrmOrigen[]>([]);
  const [tags, setTags] = useState<CrmTag[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CrmContactoListItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [o, t] = await Promise.all([
          crmCatalogoService.getOrigenes(),
          crmCatalogoService.getTags(),
        ]);
        setOrigenes(o.filter((x) => x.activo));
        setTags(t);
      } catch {
        /* catálogos opcionales al cargar lista */
      }
    })();
  }, []);

  const fetchData = useCallback(
    async (
      pageNum = page,
      pageSize = rowsPerPage,
      search = searchTerm,
    ) => {
      try {
        setLoading(true);
        const data = await crmContactoService.getAll({
          pageNumber: pageNum + 1,
          pageSize,
          searchTerm: search || undefined,
          nroVendedor: nroVendedor ?? undefined,
          origenId: origenId === '' ? undefined : origenId,
          tagId: tagId === '' ? undefined : tagId,
          soloProspectos: soloProspectos || undefined,
          activo: true,
        });
        setItems(data.items ?? []);
        setTotalCount(data.totalCount ?? 0);
      } catch (error) {
        enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar contactos'), { variant: 'error' });
        setItems([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [page, rowsPerPage, searchTerm, nroVendedor, origenId, tagId, soloProspectos, enqueueSnackbar],
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPage(0);
      fetchData(0, rowsPerPage, searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, nroVendedor, origenId, tagId, soloProspectos, rowsPerPage]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await crmContactoService.delete(deleteTarget.id);
      enqueueSnackbar('Contacto eliminado', { variant: 'success' });
      setDeleteTarget(null);
      await fetchData(page, rowsPerPage, searchTerm);
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al eliminar el contacto'), { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={2}
            >
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Contactos CRM
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Can permiso="crm.contactos.nuevo">
                  <Button
                    variant="outlined"
                    startIcon={<UploadFileIcon />}
                    onClick={() => setImportOpen(true)}
                    sx={{ textTransform: 'none' }}
                  >
                    Importar
                  </Button>
                </Can>
                <Can permiso="crm.contactos.nuevo">
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate(ROUTES.crm.contactos.nuevo)}
                  >
                    Nuevo
                  </Button>
                </Can>
              </Stack>
            </Stack>

            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', lg: 'center' }}
              flexWrap="nowrap"
            >
              <TextField
                size="small"
                placeholder="Buscar por nombre, empresa, email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ flex: 1, minWidth: { xs: '100%', lg: 280 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                select
                size="small"
                label="Origen"
                value={origenId}
                onChange={(e) => setOrigenId(e.target.value === '' ? '' : Number(e.target.value))}
                sx={{ width: { xs: '100%', lg: 160 }, flexShrink: 0 }}
              >
                <MenuItem value="">Todos</MenuItem>
                {origenes.map((o) => (
                  <MenuItem key={o.id} value={o.id}>
                    {o.nombre}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Tag"
                value={tagId}
                onChange={(e) => setTagId(e.target.value === '' ? '' : Number(e.target.value))}
                sx={{ width: { xs: '100%', lg: 140 }, flexShrink: 0 }}
              >
                <MenuItem value="">Todos</MenuItem>
                {tags.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.nombre}
                  </MenuItem>
                ))}
              </TextField>
              <VendedorSelect
                value={nroVendedor}
                onChange={(v) => setNroVendedor(v)}
                allowEmpty
                emptyOptionLabel="Todos los vendedores"
                size="small"
                sx={{ width: { xs: '100%', lg: 220 }, flexShrink: 0 }}
              />
              <FormControlLabel
                sx={{ flexShrink: 0, m: 0, whiteSpace: 'nowrap' }}
                control={
                  <Switch
                    checked={soloProspectos}
                    onChange={(e) => setSoloProspectos(e.target.checked)}
                    size="small"
                  />
                }
                label="Solo prospectos"
              />
            </Stack>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Empresa</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Teléfono</TableCell>
                    <TableCell>Vendedor</TableCell>
                    <TableCell>Origen</TableCell>
                    <TableCell>Tags</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((c) => (
                    <TableRow
                      key={c.id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'rgba(25,118,210,0.04)' },
                      }}
                      onClick={() => navigate(ROUTES.crm.contactos.editar(c.id))}
                    >
                      <TableCell>
                        <Stack direction="row" spacing={1.25} alignItems="center">
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              fontSize: 13,
                              fontWeight: 700,
                              bgcolor: '#1976d2',
                            }}
                          >
                            {initialsFromName(c.nombre)}
                          </Avatar>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {c.nombre}
                            </Typography>
                            {isRecentlyCreated(c.fechaAlta) && (
                              <Chip size="small" label="Nuevo" color="primary" sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />
                            )}
                          </Stack>
                        </Stack>
                      </TableCell>
                      <TableCell>{c.empresa || '-'}</TableCell>
                      <TableCell>{c.email || '-'}</TableCell>
                      <TableCell>{c.telefono || '-'}</TableCell>
                      <TableCell>{c.nombreVendedor || c.nroVendedor}</TableCell>
                      <TableCell>{c.origenNombre || '-'}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {(c.tags ?? []).map((t) => (
                            <Chip
                              key={t.id}
                              size="small"
                              label={t.nombre}
                              sx={t.color ? { bgcolor: t.color, color: '#fff' } : undefined}
                            />
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {c.nroCliente != null
                          ? `${c.nroCliente}${c.nombreCliente ? ` — ${c.nombreCliente}` : ''}`
                          : 'Prospecto'}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ whiteSpace: 'nowrap' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Can permiso="crm.contactos.editar">
                          <IconButton
                            size="small"
                            color="primary"
                            title="Editar"
                            onClick={() => navigate(ROUTES.crm.contactos.editar(c.id))}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Can>
                        <Can permiso="crm.contactos.editar">
                          <IconButton
                            size="small"
                            color="error"
                            title="Eliminar"
                            onClick={() => setDeleteTarget(c)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Can>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!loading && items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <Stack alignItems="center" spacing={1.5} sx={{ py: 5 }}>
                          <PersonOutlineIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
                          <Typography variant="body2" color="text.secondary">
                            No se encontraron contactos.
                          </Typography>
                          <Can permiso="crm.contactos.nuevo">
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => navigate(ROUTES.crm.contactos.nuevo)}
                            >
                              Crear primer contacto
                            </Button>
                          </Can>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(_, newPage) => {
                setPage(newPage);
                fetchData(newPage, rowsPerPage, searchTerm);
              }}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                const size = parseInt(e.target.value, 10);
                setRowsPerPage(size);
                setPage(0);
                fetchData(0, size, searchTerm);
              }}
              rowsPerPageOptions={[10, 20, 50]}
              labelRowsPerPage="Filas:"
            />
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)}>
        <DialogTitle>Eliminar contacto</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Eliminar el contacto {deleteTarget?.nombre}? Dejará de aparecer en el listado.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancelar
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <CrmContactosImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => {
          void fetchData(0, rowsPerPage, searchTerm);
          setPage(0);
        }}
      />
    </Box>
  );
};

export default CrmContactos;
